/**
 * Skills tab configuration: REST endpoints and UI flags/messages for the Service Provider
 * Skills flow. The same components run on the main provider Skills tab and on the nested
 * Location → System Settings → Skills view; `skillsConfig` is provided by
 * ServiceProviderScopeProvider and read via useServiceProviderScope().
 *
 * Maintainer map — where to edit:
 *
 * - **One place per feature** → `SKILLS_FEATURE_DEFINITIONS` below. Optional `buildApi`
 *   with `serviceProvider` and `location` branches when the feature needs REST URLs; plus
 *   optional `buildMessages` for scope-specific copy.
 *
 * - Keys under `config.api` / `config.ui.messages` must match what Skills components read
 *   (e.g. IndustriesServicedSelector → `industriesServicedSelector`).
 *
 * - **Wiring / hideTitle** → `buildSkillsConfig` (export) and
 *   ServiceProviderScopeContext.jsx for `hideTitle`.
 *
 * Checklist when adding a new Skills feature:
 * 1. Add a registry entry with `buildApi` (both branches) when URLs are needed, and `buildMessages` if needed.
 * 2. In the component, use `config.api.<key>…` and/or `config.ui.messages.<key>…`.
 */

// ---------------------------------------------------------------------------
// Path roots (shared by registry `buildApi` functions)
// ---------------------------------------------------------------------------

function serviceProviderBase(apiBaseUrl, serviceProviderId) {
  return `${apiBaseUrl}/sxcl/v1/service-providers/${serviceProviderId}`;
}

function locationBase(apiBaseUrl, serviceProviderId, locationId) {
  return `${serviceProviderBase(apiBaseUrl, serviceProviderId)}/locations/${locationId}`;
}

function scopedMessage(
  isLocation,
  locationDescriptor,
  serviceProviderDescriptor,
) {
  return isLocation ? locationDescriptor : serviceProviderDescriptor;
}

// ---------------------------------------------------------------------------
// Per-feature registry — edit one object per feature
// ---------------------------------------------------------------------------

const SKILLS_FEATURE_DEFINITIONS = {
  industriesServicedSelector: {
    buildApi: {
      serviceProvider: ({ sp }) => ({
        listBoxLeft: `${sp}/allowed-industries`,
        listBoxRight: `${sp}/service-industries`,
        listBoxAdd: `${sp}/service-industries`,
        listBoxRemove: `${sp}/service-industries`,
      }),
      location: ({ loc }) => ({
        listBoxLeft: `${loc}/industries`,
        listBoxRight: `${loc}/excluded-industries`,
        listBoxAdd: `${loc}/excluded-industries`,
        listBoxRemove: `${loc}/excluded-industries`,
      }),
    },
    buildMessages: isLocation => ({
      leftTitle: scopedMessage(
        isLocation,
        {
          id: 'SERVICE_PROVIDER_LOCATION_SKILLS_INDUSTRIES_AVAILABLE_HEADING',
          defaultMessage: 'Industries Available',
        },
        {
          id: 'SERVICE_PROVIDER_SKILLS_INDUSTRIES_SERVICED_HEADING',
          defaultMessage: 'Industries List',
        },
      ),
      rightTitle: scopedMessage(
        isLocation,
        {
          id: 'SERVICE_PROVIDER_LOCATION_SKILLS_INDUSTRIES_EXCLUDED_HEADING',
          defaultMessage: 'Industries Excluded',
        },
        {
          id: 'SERVICE_PROVIDER_SKILLS_INDUSTRIES_SERVICED_HEADING',
          defaultMessage: 'Industries Serviced',
        },
      ),
      saveSuccess: scopedMessage(
        isLocation,
        {
          id: 'SERVICE_PROVIDER_LOCATION_SKILLS_INDUSTRIES_SAVE_SUCCESS',
          defaultMessage:
            'Service Provider Location industries added to excluded list successfully',
        },
        {
          id: 'SERVICE_PROVIDER_SKILLS_INDUSTRIES_SAVE_SUCCESS',
          defaultMessage: 'Service Provider industries were saved successfully',
        },
      ),
      removeSuccess: scopedMessage(
        isLocation,
        {
          id: 'SERVICEPROVIDER_LOCATION_SKILLS_INDUSTRIES_REMOVE_SUCCESS',
          defaultMessage:
            'Service Provider Location industries removed from excluded list successfully',
        },
        {
          id: 'SERVICEPROVIDER_SKILLS_INDUSTRIES_REMOVE_SUCCESS',
          defaultMessage:
            'Service Provider industries were removed successfully',
        },
      ),
    }),
  },
  industriesServiced: {
    buildApi: {
      serviceProvider: ({ sp }) => ({
        fetch: `${sp}/service-industries`,
      }),
      location: ({ loc }) => ({
        fetch: `${loc}/industries`,
      }),
    },
  },
  productLinesSelector: {
    buildApi: {
      serviceProvider: ({ sp }) => ({
        listBoxLeft: `${sp}/product-lines`,
        listBoxRight: `${sp}/excluded-product-lines`,
        listBoxAdd: `${sp}/excluded-product-lines`,
        listBoxRemove: `${sp}/excluded-product-lines`,
      }),
      location: ({ loc }) => ({
        listBoxLeft: `${loc}/product-lines`,
        listBoxRight: `${loc}/excluded-product-lines`,
        listBoxAdd: `${loc}/excluded-product-lines`,
        listBoxRemove: `${loc}/excluded-product-lines`,
      }),
    },
    buildMessages: isLocation => ({
      saveSuccess: scopedMessage(
        isLocation,
        {
          id: 'SERVICEPROVIDER_LOCATION_SKILLS_PRODUCT_LINES_SAVE_SUCCESS',
          defaultMessage:
            'Service Provider Location product lines were saved successfully',
        },
        {
          id: 'SERVICEPROVIDER_SKILLS_PRODUCT_LINES_SAVE_SUCCESS',
          defaultMessage:
            'Service Provider product lines were saved successfully',
        },
      ),
      removeSuccess: scopedMessage(
        isLocation,
        {
          id: 'SERVICEPROVIDER_LOCATION_SKILLS_PRODUCT_LINES_REMOVE_SUCCESS',
          defaultMessage:
            'Service Provider Location product lines were removed successfully',
        },
        {
          id: 'SERVICEPROVIDER_SKILLS_PRODUCT_LINES_REMOVE_SUCCESS',
          defaultMessage:
            'Service Provider product lines were removed successfully',
        },
      ),
    }),
  },
  brandsSelector: {
    buildApi: {
      serviceProvider: ({ sp }) => ({
        listBoxLeft: `${sp}/brands`,
        listBoxRight: `${sp}/excluded-brands`,
        listBoxAdd: `${sp}/excluded-brands`,
        listBoxRemove: `${sp}/excluded-brands`,
      }),
      location: ({ loc }) => ({
        listBoxLeft: `${loc}/brands`,
        listBoxRight: `${loc}/excluded-brands`,
        listBoxAdd: `${loc}/excluded-brands`,
        listBoxRemove: `${loc}/excluded-brands`,
      }),
    },
    buildMessages: isLocation => ({
      saveSuccess: scopedMessage(
        isLocation,
        {
          id: 'SERVICEPROVIDER_LOCATION_SKILLS_BRANDS_SAVE_SUCCESS',
          defaultMessage:
            'Service Provider Location brands were saved successfully',
        },
        {
          id: 'SERVICEPROVIDER_SKILLS_BRANDS_SAVE_SUCCESS',
          defaultMessage: 'Service Provider brands were saved successfully',
        },
      ),
      removeSuccess: scopedMessage(
        isLocation,
        {
          id: 'SERVICEPROVIDER_LOCATION_SKILLS_BRANDS_REMOVE_SUCCESS',
          defaultMessage:
            'Service Provider Location brands were removed successfully',
        },
        {
          id: 'SERVICEPROVIDER_SKILLS_BRANDS_REMOVE_SUCCESS',
          defaultMessage: 'Service Provider brands were removed successfully',
        },
      ),
    }),
  },
  skills: {
    buildMessages: isLocation => ({
      heading: scopedMessage(
        isLocation,
        {
          id: 'SERVICE_PROVIDER_LOCATION_SKILLS_EDIT_HEADING',
          defaultMessage: 'Service Provider Location Skills',
        },
        {
          id: 'SERVICE_PROVIDER_SKILLS_EDIT_HEADING',
          defaultMessage: 'Service Provider Skills',
        },
      ),
    }),
  },
  setup: {
    buildApi: {
      serviceProvider: ({ sp }) => ({
        fetchEmail: `${sp}/notifications/emails`,
        fetchPhones: `${sp}/notifications/phones`,
        editEmail: `${sp}/notifications/emails`,
        editPhones: `${sp}/notifications/phones`,
      }),
      location: ({ loc }) => ({
        fetchEmail: `${loc}/notifications/emails`,
        fetchPhones: `${loc}/notifications/phones`,
        editEmail: `${loc}/notifications/emails`,
        editPhones: `${loc}/notifications/phones`,
      }),
    },
    buildMessages: isLocation => ({
      setupSaveSuccess: scopedMessage(
        isLocation,
        {
          id: 'SERVICEPROVIDER_LOCATION_NOTIFICATION_SAVE_SUCCESS',
          defaultMessage: 'Service Provider Location Setup saved successfully',
        },
        {
          id: 'SERVICEPROVIDER_NOTIFICATION_SAVE_SUCCESS',
          defaultMessage: 'Service Provider Setup saved successfully',
        },
      ),
      setupSaveWarning: scopedMessage(
        isLocation,
        {
          id: 'SERVICEPROVIDER_LOCATION_NOTIFICATION_SAVE_WARNING',
          defaultMessage: 'Service Provider Location Setup saved with warnings',
        },
        {
          id: 'SERVICEPROVIDER_NOTIFICATION_SAVE_WARNING',
          defaultMessage: 'Service Provider Setup saved with warnings',
        },
      ),
    }),
  },
};

// ---------------------------------------------------------------------------
// Merge registry → api / ui.messages
// ---------------------------------------------------------------------------

function buildSkillsApiFromRegistry(ctx, isLocation) {
  const branch = isLocation ? 'location' : 'serviceProvider';
  return Object.fromEntries(
    Object.entries(SKILLS_FEATURE_DEFINITIONS)
      .filter(([, def]) => typeof def.buildApi?.[branch] === 'function')
      .map(([key, def]) => [key, def.buildApi[branch](ctx)]),
  );
}

function buildSkillsMessagesFromRegistry(isLocation) {
  return Object.fromEntries(
    Object.entries(SKILLS_FEATURE_DEFINITIONS)
      .filter(([, def]) => typeof def.buildMessages === 'function')
      .map(([key, def]) => [key, def.buildMessages(isLocation)]),
  );
}

// ---------------------------------------------------------------------------
// Exported factory
// ---------------------------------------------------------------------------

/**
 * Builds `skillsConfig` for Service Provider Skills (provider tab vs location scope).
 *
 * `isLocation` is derived from `locationId` and must stay aligned with
 * deriveServiceProviderScope() in ./utils.js (same non-empty locationId check).
 *
 * @param {object} options
 * @param {string} options.apiBaseUrl
 * @param {string} options.serviceProviderId
 * @param {string|null|undefined} options.locationId — when set, location API URLs and location copy apply
 * @param {boolean} options.hideTitle — page section title; set in ServiceProviderScopeContext
 * @returns {{
 *   api: Record<string, Record<string, string>>,
 *   ui: { hideTitle: boolean, messages: ReturnType<typeof buildSkillsMessagesFromRegistry> },
 * }}
 * @see ServiceProviderScopeContext.jsx — buildSkillsConfig call site and hideTitle
 */
function buildSkillsConfig({
  apiBaseUrl,
  serviceProviderId,
  locationId,
  hideTitle,
}) {
  const isLocation = locationId != null && locationId !== '';

  const ctx = {
    sp: serviceProviderBase(apiBaseUrl, serviceProviderId),
    loc: isLocation
      ? locationBase(apiBaseUrl, serviceProviderId, locationId)
      : '',
  };

  return {
    api: buildSkillsApiFromRegistry(ctx, isLocation),
    ui: {
      hideTitle,
      messages: buildSkillsMessagesFromRegistry(isLocation),
    },
  };
}

export default buildSkillsConfig;
